import InfoCircleOutlined from "@ant-design/icons/lib/icons/InfoCircleOutlined";
import { Alert, Button, Form, message, Modal, Select, Typography } from "antd";
import axios from "axios";
import { CompanyContact } from "interfaces/companies";
import { Company } from "interfaces/company";
import { useCallback, useEffect, useState } from "react";

interface Props {
  company: Company;
  onClose: () => void;
}

const ResetPasswordModal: React.FC<Props> = ({ company, onClose }) => {
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    axios
      .get<CompanyContact[]>(`/companies/${company.id}/contact-persons/`)
      .then(response => {
        setContacts(response.data);
        setLoadingContacts(false);
      })
      .catch(() => setLoadingContacts(false));
  }, [company.id]);

  const onSubmit = useCallback(
    (values: { contact_person: number }) => {
      axios
        .post(`/companies/${company.id}/reset-password/`, {
          contact_person: values.contact_person,
        })
        .then(resp => {
          message.success(`Nytt passord ble sendt til ${resp.data.recipient}`);
          onClose();
        })
        .catch(() => {
          message.error("Feil ved tilbakestilling av passord");
        })
        .finally(() => setSubmitting(false));
    },
    [company.id, onClose]
  );

  return (
    <Modal open title={`Tilbakestill passord for ${company.name}`} onCancel={onClose} footer={null}>
      <Typography.Paragraph>
        Velg en kontaktperson under for å tilbakestille passordet til bedriften. Kontaktpersonen vil da motta et
        midlertidig passord på e-post med instruksjoner om å logge seg inn og endre passordet.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary">
        Dersom du ikke finner ønsket person i listen, må du først legge de inn som kontaktperson.
      </Typography.Paragraph>
      <Form layout="vertical" onFinish={onSubmit} disabled={loadingContacts}>
        <Form.Item label="Kontaktperson">
          <Form.Item name="contact_person" noStyle>
            <Select
              showSearch
              placeholder="Velg kontaktperson"
              optionFilterProp="children"
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={contacts.map(contact => ({
                label: `${contact.first_name} ${contact.last_name} (${contact.email})`,
                value: contact.id,
              }))}
            />
          </Form.Item>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Tilbakestill passord
          </Button>
        </Form.Item>
        <Alert message="NB: Dette vil endre passordet for hele bedriften." type="info" icon={<InfoCircleOutlined />} />
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal;
